/**
 * Route one spatial marker activation from its stable subject identity.
 *
 * The renderer knows geometry; this module knows domain navigation. A marker is never assumed to
 * be a CML: CML/TML keep their anchored popup, Forge-tag subjects use the existing generic Tag
 * details flow, and every unknown kind fails closed without guessing an endpoint.
 */

import store from '@/store';
import { openPopupCard, closePopupCard } from '@/composables/usePopupCard';
import { SpatialTagTypeOptions } from '@/features/DataSources/model/definitionVocabulary';
import { warn, error } from '@/utils/debugLogger';

function normalizedKind(subject) {
  return subject && subject.kind != null ? String(subject.kind).trim() : '';
}

const CURRENT_FORGE_TAG_KINDS = new Set(['anomalytag', 'inspectiontag', 'mastertag']);
const CANONICAL_TAG_TYPES = new Set(SpatialTagTypeOptions.map(option => option.value));
const NATIVE_TAG_DETAILS_TYPES = new Set([
  'equipmentTag',
  'pipelineTag',
  'structuralTag',
  'anomalyTag',
  'inspectionTag',
  'annotationTag',
  'scaffoldTag',
  'permitTag',
  'containerTag',
]);

/** A broad `tag` kind is not authorization; generic subjects need a server-owned subtype. */
function expectedForgeTagKind(subject) {
  const kind = normalizedKind(subject);
  if (CURRENT_FORGE_TAG_KINDS.has(kind.toLowerCase())) return kind;

  if (subject?.kind !== 'tag' || subject?.family !== 'tag') return '';
  const { tagType } = subject;
  return CANONICAL_TAG_TYPES.has(tagType) && NATIVE_TAG_DETAILS_TYPES.has(tagType)
    ? tagType
    : '';
}

function notifyUser(level, message) {
  const dialog = store.state.mainApp?.$dialog
    || (typeof window !== 'undefined' ? window.$dialog : null);
  dialog?.notify?.[level]?.(message, { position: 'top-right', timeout: 4000 });
}

function subjectIdentity(subject) {
  const id = subject?.id == null ? '' : String(subject.id).trim();
  return {
    kind: normalizedKind(subject),
    id,
    label: String(subject?.displayLabel || id || 'this marker').trim(),
  };
}

function refuseActivation(subject, message) {
  warn('[SpatialMarkerActivation] marker details unavailable', subject);
  notifyUser('warning', message);
}

function openEntityPopup(entityType, id, anchorEl, sourceViewer) {
  openPopupCard(entityType, id, anchorEl, {
    sourceViewer,
    preserveSpatialScene: true,
  });
}

async function openForgeTag(identity, expectedKind) {
  try {
    const tag = await store.dispatch('GET_BASE_TAG_BY_ID', {
      id: identity.id,
      assetId: store.state.assetId,
      expectedKind,
    });
    closePopupCard();
    await store.dispatch('OPEN_TAG', {
      tag: tag?.clone ? tag.clone() : tag,
      clearPrevious: false,
    });
  } catch (err) {
    error('[SpatialMarkerActivation] failed to open Forge tag', { ...identity, err });
    notifyUser('warning', `Could not open details for ${identity.label}.`);
  }
}

/**
 * @param {object} input
 * @param {{kind:string,id:string,displayLabel?:string}} input.subject
 * @param {HTMLElement} [input.anchorEl]
 * @param {'3d'|'360'|'doc'} [input.sourceViewer='3d']
 * @returns {Promise<void>}
 */
export async function activateSpatialMarkerSubject({
  subject,
  anchorEl = null,
  sourceViewer = '3d',
} = {}) {
  const identity = subjectIdentity(subject);

  if (!identity.kind || !identity.id) {
    refuseActivation(subject, 'Details are not available for this marker.');
    return;
  }

  if (identity.kind.toLowerCase() === 'cml') {
    openEntityPopup('cml', identity.id, anchorEl, sourceViewer);
    return;
  }

  if (identity.kind.toLowerCase() === 'tml') {
    openEntityPopup('tmlpoint', identity.id, anchorEl, sourceViewer);
    return;
  }

  const expectedKind = expectedForgeTagKind(subject);
  if (expectedKind) {
    await openForgeTag(identity, expectedKind);
    return;
  }

  refuseActivation(subject, `Details are not available for ${identity.label}.`);
}
